import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  BrainCircuit,
  Code2,
  GraduationCap,
  Layers3,
  LockKeyhole,
  MessageSquareText,
  Microscope,
  ShieldCheck,
  Sparkles,
  Workflow
} from "lucide-react";
import Navbar from "../components/Navbar";
import BottomContent from "../components/BottomContent";
import ScrollButton from "../components/ScrollButton";
import Thank from "../components/Thank";
import DarkModeButton from "../components/DarkModeButton";
import ContactButton from "../components/ContactButton";
import MolecularLogo from "../assets/logo/MolecularScix.jpeg";
import QuantumLogo from "../assets/logo/Quantum.AI.png";
import MantraiLogo from "../assets/logo/MantraiLogo.png";
import SandboxLogo from "../assets/logo/SandboxLogo.png";
import GuruLogo from "../assets/logo/GuruLogo.png";
import VidyaLogo from "../assets/logo/VidyaLogo.png";
import SurakshaLogo from "../assets/logo/SurakshaLogo.png";
import "./Product.css";

const products = [
  {
    id: "molecularscix",
    imageURL: MolecularLogo,
    productName: "MolecularSciX.ai",
    category: "Molecular R&D",
    icon: Microscope,
    accent: "teal",
    productDescription:
      "MolecularSciX.ai is an AI-driven molecular science platform designed to transform chemical research, pharma discovery, and molecular optimization. It combines reaction prediction, retrosynthesis planning, spectroscopy AI, molecular simulation, and bio-molecular design to help researchers accelerate discovery, reduce experimental risk, and make data-driven decisions from design to synthesis.",
    point: "AI-powered unified platform for faster, smarter, and cost-efficient Molecular R&D.",
    features: ["Reaction prediction", "Retrosynthesis planning", "Molecular simulation"],
  },
  {
    id: "quantum-ai",
    imageURL: QuantumLogo,
    productName: "Quantum.AI",
    category: "Private GenAI",
    icon: LockKeyhole,
    accent: "blue",
    productDescription:
      "Quantum.AI is a local Generative AI solution designed to deliver secure and adaptive data intelligence without external exposure. It leverages the contextual power of RAG and the autonomy of Agentic AI to optimize operations, enhance customer engagement, and drive innovation while maintaining complete data privacy.",
    point: "Local Generative AI solution for secure data intelligence and operational efficiency.",
    features: ["Local AI intelligence", "RAG workflows", "Agentic automation"],
  },
  {
    id: "mantrai",
    imageURL: MantraiLogo,
    productName: "MantrAI",
    category: "Developer Productivity",
    icon: Code2,
    accent: "orange",
    productDescription:
      "MantrAI is an AI-powered developer tool designed to accelerate coding and streamline the development process. It assists developers with intelligent code generation, real-time suggestions, debugging support, and best practice recommendations, enhancing productivity and efficiency in software development.",
    point: "AI-powered coding assistant for code generation, debugging, and development workflow optimization.",
    features: ["Code generation", "Debugging support", "Best-practice guidance"],
  },
  {
    id: "llm-sandbox",
    imageURL: SandboxLogo,
    productName: "LLM Sandbox",
    category: "Model Engineering",
    icon: Layers3,
    accent: "green",
    productDescription:
      "LLM Sandbox is a specialized platform designed to enhance engineering productivity by providing a customized environment for building, testing, and deploying LLM-driven solutions. It empowers developers with the flexibility to experiment, prototype, and optimize models tailored to organizational needs, ensuring efficient integration and iteration.",
    point: "Customizable platform for developing, testing, and deploying LLM-based solutions.",
    features: ["Prototype faster", "Test models", "Deploy LLM apps"],
  },
  {
    id: "guru",
    imageURL: GuruLogo,
    productName: "Guru",
    category: "AI Assistant",
    icon: MessageSquareText,
    accent: "blue",
    productDescription:
      "Guru is an advanced AI-powered chatbot designed to enhance customer engagement and support. With voice and text-based interactions, it provides intelligent responses, automates queries, and seamlessly integrates with various platforms, ensuring an efficient and personalized user experience.",
    point: "AI-powered chatbot with voice & text-based interactions, intelligent automation, and seamless integration.",
    features: ["Voice and text", "Smart responses", "Platform integration"],
  },
  {
    id: "vidya",
    imageURL: VidyaLogo,
    productName: "Vidya",
    category: "Education AI",
    icon: GraduationCap,
    accent: "teal",
    productDescription:
      "Vidya revolutionizes education with AI-driven insights, personalized learning paths, and intelligent tutoring. It enhances student engagement, automates assessments, and provides educators with deep analytics to optimize teaching strategies and improve learning outcomes.",
    point: "AI-powered education system with personalized learning, intelligent tutoring, and automated assessments.",
    features: ["Personalized learning", "Intelligent tutoring", "Assessment automation"],
  },
  {
    id: "suraksha",
    imageURL: SurakshaLogo,
    productName: "Suraksha",
    category: "Security AI",
    icon: ShieldCheck,
    accent: "red",
    productDescription:
      "Suraksha is an advanced AI-powered security solution engineered to protect applications, data, and digital infrastructure. It features cutting-edge authentication, real-time threat monitoring, and smooth integration with existing security setups to deliver complete protection against cyber risks.",
    point: "AI-powered security solution with robust authentication, real-time threat monitoring, and effortless integration.",
    features: ["Authentication", "Threat monitoring", "Security integration"],
  },
];

const capabilities = [
  "Ready-to-use AI products",
  "Secure enterprise integration",
  "Automation-led productivity",
  "Built for business growth",
];

export default function Product() {
  const featuredProducts = products.slice(0, 3);

  return (
    <>
      <Navbar />

      <main className="product-page">
        <section className="product-hero-new">
          <div className="product-container product-hero-grid">
            <div className="product-hero-copy">
              <span className="product-eyebrow">
                <Sparkles size={17} />
                Products
              </span>
              <h1>Next-gen AI products built for real business impact</h1>
              <p>
                Explore ready-to-use Vedalytics products designed to automate workflows, improve decision-making, strengthen security, and accelerate growth across research, enterprise AI, education, development, and customer engagement.
              </p>
              <div className="product-hero-actions">
                <Link to="/scheduleDemo" className="product-primary-link">
                  Schedule a Demo
                  <ArrowRight size={18} />
                </Link>
                <a href="#product-catalog" className="product-secondary-link">
                  Explore Products
                </a>
              </div>
            </div>

            <div className="product-orbit" aria-hidden="true">
              <div className="product-orbit-center">
                <BrainCircuit size={48} />
                <span>AI Product Suite</span>
              </div>
              {featuredProducts.map((product, index) => {
                const Icon = product.icon;
                return (
                  <div className={`product-orbit-node node-${index + 1}`} key={product.id}>
                    <img src={product.imageURL} alt="" />
                    <Icon size={18} />
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="product-capability-band">
          <div className="product-container product-capability-grid">
            {capabilities.map((capability) => (
              <div className="product-capability" key={capability}>
                <BadgeCheck size={20} />
                <span>{capability}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="product-catalog-section" id="product-catalog">
          <div className="product-container">
            <div className="product-section-heading">
              <span>Product Portfolio</span>
              <h2>AI solutions for teams that need speed, intelligence, and trust</h2>
            </div>

            <div className="product-grid-new">
              {products.map((product) => {
                const Icon = product.icon;
                return (
                  <article className={`product-card-new accent-${product.accent}`} id={product.id} key={product.id}>
                    <div className="product-card-top">
                      <div className="product-logo-box">
                        <img src={product.imageURL} alt={product.productName} loading="lazy" />
                      </div>
                      <div className="product-icon-chip">
                        <Icon size={21} />
                      </div>
                    </div>
                    <span className="product-category">{product.category}</span>
                    <h3>{product.productName}</h3>
                    <strong>{product.point}</strong>
                    <p>{product.productDescription}</p>
                    <div className="product-feature-list">
                      {product.features.map((feature) => (
                        <span key={feature}>
                          <BadgeCheck size={15} />
                          {feature}
                        </span>
                      ))}
                    </div>
                    <Link to="/scheduleDemo" className="product-card-link">
                      Learn More
                      <ArrowRight size={17} />
                    </Link>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="product-showcase-strip">
          <div className="product-container product-showcase-grid">
            <div>
              <span className="product-eyebrow">
                <Workflow size={17} />
                Built to Scale
              </span>
              <h2>From specialized AI tools to enterprise-ready platforms</h2>
            </div>
            <p>
              Vedalytics products are designed around practical adoption: clear workflows, secure integrations, faster experimentation, and measurable outcomes for teams that want AI to move from concept to daily operations.
            </p>
          </div>
        </section>
      </main>

      <Thank />
      <BottomContent />
      <ContactButton />
      <DarkModeButton />
      <ScrollButton />
    </>
  );
}
